import type { DraftStatus } from '../../../shared/enums';
import type { DraftWithLead } from '../../../shared/types';
import { shell } from '../app';
import { NetworkError } from '../data/api';
import { copyText, listDrafts, setDraftStatus } from '../data/drafts';
import { afterRemoval, stepSelection } from '../logic/desk';
import { isDesktop } from '../ui/breakpoint';
import { empty, linkBtn } from '../ui/components';
import { h, replaceChildren, span } from '../ui/dom';
import { isTypingEvent } from '../ui/keys';
import type { View } from '../ui/router';
import { draftBody, draftMeta } from './emails/draftParts';
import type { DraftActions } from './emails/draftParts';

/** Emails tab (M3): pending drafts to copy into the work mailbox. The app only DRAFTS —
 *  sending always happens from the rep's real email account. On the desktop the list and the open
 *  draft sit side by side (D65). */

type Filter = DraftStatus | 'all';

export const emailsView: View = ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Emails', {
    action: linkBtn('Templates', '#/templates', { variant: 'subtle', size: 'sm' }),
  });
  const host = h('div', { class: 'page page--emails' });
  root.appendChild(host);

  let filter: Filter = 'pending';
  let openId: string | null = null;
  let drafts: DraftWithLead[] = [];
  let disposed = false;
  const desk = isDesktop();

  const filters: { value: Filter; label: string }[] = [
    { value: 'pending', label: 'Pending' },
    { value: 'copied', label: 'Copied' },
    { value: 'dismissed', label: 'Dismissed' },
    { value: 'all', label: 'All' },
  ];

  const filterBar = h(
    'div',
    { class: 'chips chips--filters' },
    filters.map((f) =>
      h(
        'button',
        {
          type: 'button',
          class: 'chip',
          'aria-pressed': String(f.value === filter),
          onClick: (ev: MouseEvent) => {
            filter = f.value;
            for (const b of filterBar.querySelectorAll('button')) {
              b.setAttribute('aria-pressed', String(b === ev.currentTarget));
            }
            void load();
          },
        },
        f.label,
      ),
    ),
  );
  // painted at once: on bad signal the first answer can take the whole 20 s timeout
  const list = h('div', { class: 'drafts' }, empty('Loading drafts…'));
  const detail = h('div', { class: 'split__detail' });

  /** On the desktop a draft that leaves the list (dismissed, restored) hands the selection to its
   *  neighbour, as Mail does (D65); a copied one stays open (copyAndMark). */
  const handOn = (d: DraftWithLead, status: DraftStatus) => {
    if (desk && filter !== 'all' && status !== filter)
      openId = afterRemoval(
        drafts.map((x) => x.id),
        d.id,
      );
  };

  const copyAndMark = async (d: DraftWithLead, text: string, what: string) => {
    const ok = await copyText(text);
    if (!ok) {
      shell.toast('Could not copy — select the text manually', { kind: 'error' });
      return;
    }
    shell.toast(`${what} copied — paste it into your work email`, { kind: 'success' });
    if (d.status === 'pending') {
      try {
        await setDraftStatus(d.id, 'copied');
        // the copied draft stays open until the rep moves on (on the phone too), so the next copy
        // is from the same email; the next filter tap reloads the list
        drafts = drafts.map((x) => (x.id === d.id ? { ...x, status: 'copied' as const } : x));
        render();
      } catch {
        // copy already succeeded; status update failing is not worth alarming the user
      }
    }
  };

  const changeStatus = async (d: DraftWithLead, status: DraftStatus) => {
    try {
      await setDraftStatus(d.id, status);
      handOn(d, status);
      await load();
    } catch (err) {
      shell.toast(
        err instanceof NetworkError
          ? 'No signal — try again when you have a connection.'
          : err instanceof Error
            ? err.message
            : String(err),
        { kind: 'error' },
      );
    }
  };

  const actionsFor = (d: DraftWithLead): DraftActions => ({
    copy: (text, what) => void copyAndMark(d, text, what),
    setStatus: (s) => void changeStatus(d, s),
  });

  const draftCard = (d: DraftWithLead): HTMLElement => {
    const open = openId === d.id;
    return h(
      'article',
      { class: ['draft', open ? 'draft--open' : '', `draft--${d.status}`].filter(Boolean).join(' ') },
      h(
        'button',
        {
          type: 'button',
          class: 'draft__head',
          onClick: () => {
            openId = open ? null : d.id;
            render();
          },
        },
        h(
          'div',
          { class: 'draft__headtext' },
          h('div', { class: 'draft__lead' }, d.business_name),
          open ? null : h('div', { class: 'draft__preview' }, d.subject), // scan without opening
          draftMeta(d),
        ),
        span('draft__chev', open ? '▾' : '▸'),
      ),
      open ? draftBody(d, actionsFor(d)) : null,
    );
  };

  const draftRow = (d: DraftWithLead): HTMLElement =>
    h(
      'button',
      {
        type: 'button',
        class: ['draftrow', d.id === openId ? 'draftrow--selected' : ''].filter(Boolean).join(' '),
        'aria-pressed': String(d.id === openId),
        onClick: () => {
          openId = d.id;
          render();
        },
      },
      h(
        'span',
        { class: 'draftrow__text' },
        h('span', { class: 'draftrow__lead' }, d.business_name),
        h('span', { class: 'draftrow__subject' }, d.subject),
        draftMeta(d),
      ),
    );

  const emptyState = () =>
    empty(
      filter === 'pending' ? 'No pending drafts.' : `No ${filter === 'all' ? '' : filter + ' '}drafts.`,
      filter === 'pending'
        ? 'Drafts appear here when a contract gets close, or from "Draft email" on a lead.'
        : undefined,
    );

  const renderSplit = () => {
    if (!drafts.some((d) => d.id === openId)) openId = drafts[0]?.id ?? null;
    const current = drafts.find((d) => d.id === openId) ?? null;
    replaceChildren(list, drafts.length ? drafts.map(draftRow) : emptyState());
    replaceChildren(
      detail,
      current
        ? [
            h('h2', { class: 'draft__heading' }, current.business_name),
            draftMeta(current),
            draftBody(current, actionsFor(current)),
          ]
        : null,
    );
  };

  const render = () => {
    for (const b of filterBar.querySelectorAll('button')) {
      b.setAttribute(
        'aria-pressed',
        String(b.textContent === filters.find((f) => f.value === filter)?.label),
      );
    }
    if (desk) return renderSplit();
    replaceChildren(list, drafts.length ? drafts.map(draftCard) : emptyState());
  };

  let loadSeq = 0;
  const load = async () => {
    // Switching filters quickly: only the latest request may paint, or a slow earlier one shows
    // the wrong drafts under the selected chip.
    const mine = ++loadSeq;
    try {
      const fresh = await listDrafts(filter === 'all' ? {} : { status: filter });
      if (disposed || mine !== loadSeq) return;
      drafts = fresh;
      render();
    } catch (err) {
      if (disposed || mine !== loadSeq) return;
      drafts = []; // nothing stale for the arrows to paint over the error
      replaceChildren(detail);
      replaceChildren(
        list,
        empty(
          err instanceof NetworkError ? 'Emails need a connection.' : 'Could not load drafts.',
          err instanceof NetworkError
            ? 'Your queued visits still sync — drafts will load when you have signal.'
            : err instanceof Error
              ? err.message
              : undefined,
        ),
      );
    }
  };

  const onKey = (e: KeyboardEvent) => {
    if ((e.key !== 'ArrowDown' && e.key !== 'ArrowUp') || isTypingEvent(e) || !drafts.length) return;
    e.preventDefault();
    openId = stepSelection(
      drafts.map((d) => d.id),
      openId,
      e.key === 'ArrowDown' ? 1 : -1,
    );
    render();
  };
  if (desk) {
    host.classList.add('split');
    list.classList.add('split__rows');
    replaceChildren(
      host,
      h('div', { class: 'split__list' }, h('div', { class: 'split__head' }, filterBar), list),
      detail,
    );
    document.addEventListener('keydown', onKey);
  } else replaceChildren(host, filterBar, list);
  void load();
  return () => {
    disposed = true;
    document.removeEventListener('keydown', onKey);
  };
};
